<template>
  <TopHeader class="character-detail__header pc-only" />
  <div class="character-detail">
    <DetailDescription :character="calData" />
    <CreationGuideBar
      :character="calData"
      :is2DHuman="is2DHuman"
      @create-work="createWork(SOURCE.CHARACTER_DETAIL)"
    />
    <ImageDisplay
      :character="calData"
      :is2DHuman="is2DHuman"
      @create-work="createWork(SOURCE.CHARACTER_DETAIL)"
    />
    <ImageComposition
      v-if="!is2DHuman"
      :character="calData"
      :character-xihe="xiheData?.data"
      @create-work="createWork(SOURCE.CHARACTER_DETAIL)"
    />
    <RecommendedAvatar
      v-if="calData?.recommended_data?.length"
      :digitalHumanList="calData?.recommended_data"
    />
    <ShareM />
    <footer class="pc-only">
      <StartRightNow :retractable="false" :handler="handleTrail" />
      <AppFooter :retractable="false" />
    </footer>
  </div>
</template>
<script lang="ts" setup>
import { usePayment } from '@rendora/common/features/payment/store';
import { MODEL_TYPE } from '@rendora/common/types/enums';
import { is2DCharacter } from '@rendora/common/utils/is';
import { last } from 'lodash-es';
import { useI18n } from 'vue-i18n';
import type { Character } from '~/api/interface/character';
import { getCharacter, getCharacterFromXihe } from '~/api/module/character';
import { SESSION_KEY } from '~/constants/config';
import StartRightNow from '~/design/widgets/start-right-now/index.vue';
import TopHeader from '~/design/widgets/top-header/index.vue';
import { useCharacterCreateOps } from '~/hooks/character';
import { useWatchPageSeoData } from '~/hooks/useSeo';
import { useStartPatch } from '~/hooks/useStarPatch';
import { SOURCE } from '~/utils/track';
import DetailDescription from './components/detail-description/index.vue';
import ImageComposition from './components/image-composition/index.vue';
import ImageDisplay from './components/image-display/index.vue';
import RecommendedAvatar from './components/recommended-avatar/index.vue';
import ShareM from './components/share-m/index.vue';

const CreationGuideBar = defineAsyncComponent(() =>
  isChinese()
    ? import('./components/creation-guide-bar/index-cn.vue').then(m => m.default)
    : import('./components/creation-guide-bar/index-en.vue').then(m => m.default),
);

const AppFooter = defineAsyncComponent(() =>
  isChinese()
    ? import('~/design/widgets/bottom-footer/footer-cn.vue').then(m => m.default)
    : import('~/design/widgets/bottom-footer/footer-en.vue').then(m => m.default),
);

const route = useRoute();
const { t: $t } = useI18n();
const { createOps } = useCharacterCreateOps();
const paymentStore = usePayment();
const lookName = computed(() => last(String(route.params.look_name).split('-')));

provideResourceLocation({
  source: SOURCE.CHARACTER_DETAIL,
});

function autoSuffix(t: string) {
  const suffix = isChinese() ? `-${$t('you-yan')}AI` : `-${$t('you-yan')}`;
  if (t.endsWith(suffix)) {
    return t;
  }

  return t + suffix;
}

const { data: character, status } = useAsyncData(`${lookName.value}_backend`, async () => {
  const { data } = await getCharacter({
    look_name: lookName.value as string,
    query_star: import.meta.client,
  });
  if (data) {
    data.query_star = import.meta.client;
    const tagTreeArr = Object.values(data.tag_tree);

    const industryTag = tagTreeArr.find(tag => tag.outer_id === 'character_industry');

    const applicationTag = tagTreeArr.find(tag => tag.outer_id === 'character_application');

    const characterIndustries: string[] = [];
    const characterScenarios: string[] = [];

    if (industryTag) {
      const idArr1 = data.tag_ids[industryTag.tag_id];
      if (idArr1?.length) {
        idArr1.forEach(id => {
          const targetIndustry = Object.values(industryTag.children || []).find(
            target => target.tag_id === id,
          );
          if (targetIndustry) {
            characterIndustries.push(targetIndustry.tag_name);
          }
        });
      }
    }

    if (applicationTag) {
      const idArr2 = data.tag_ids[applicationTag.tag_id];
      if (idArr2?.length) {
        idArr2.forEach(id => {
          const targetScenario = Object.values(applicationTag?.children || []).find(
            target => target.tag_id === id,
          );
          if (targetScenario) {
            characterScenarios.push(targetScenario.tag_name);
          }
        });
      }
    }

    const industry = characterIndustries.slice(0, 3).join('、') || $t('shang-ye-fu-wu');
    const scenario = characterScenarios.slice(0, 5).join('、') || $t('nei-rong-jiang-jie');

    const description = isChinese()
      ? `大家好！我是${characterIndustries[0] || $t('shang-ye-fu-wu')}数字人${data.name}，由魔珐科技精心打造的一款${is2DCharacter(data.xhuman_type) ? '2D' : '3D'}超写实AI数字人，我可以在${industry}等行业，从事${scenario}等工作，并且还拥有${is2DCharacter(data.xhuman_type) ? '真人相似度' : '逼真'}的外观、丰富的表现力以及专业的语言表达能力，无需实拍，即可通过AI技术快速生成高质量的视频内容。无论是品牌代言、在线讲解，还是社交媒体内容创作，我都能满足。您可以在多种应用场景内容制作时候使用我，我将为您带来超乎期待的体验！`
      : `Hi, I'm ${data.name}, a ${is2DCharacter(data.xhuman_type) ? '2D' : '3D'} AI Avatar from Rendora. I help ${industry} with ${scenario}, combining a lifelike appearance with AI power to communicate expressively and professionally. Supporting 50+ languages with no filming needed, I create engaging videos for campaigns, courses, and social media. Let's create something remarkable together.`;

    data.seo_description = data.seo_description || description;

    if (!data.description) {
      data.description = description;
    }

    data.seo_title = data.seo_title
      ? autoSuffix(data.seo_title)
      : $t(
          is2DCharacter(data.xhuman_type) ? 'character-detail-title-2d' : 'character-detail-title',
          [characterIndustries[0] || $t('shang-ye-fu-wu'), data.name],
        ).trim();
  }

  deepOmit(data as any, ['look']);
  return data as Character;
});

const { calData } = useStartPatch(character, MODEL_TYPE.MEDIA);

useWatchPageSeoData(character);

const is2DHuman = computed(() => {
  return calData.value?.xhuman_type && is2DCharacter(calData.value?.xhuman_type);
});

watch(
  [() => character.value, () => status.value],
  ([iData, iStatus]) => {
    if (!iData?.id && ['success', 'error'].includes(iStatus)) {
      throw new Error(`no character found for look_name: ${lookName.value}`);
    }

    if (iData?.look_name && import.meta.client) {
      /** 获取来源，获取后删除记录 */
      const character_source = sessionGetItem(SESSION_KEY.CHARACTER_SOURCE);
      const source = character_source || SOURCE.CHARACTER_DETAIL;
      sessionRemoveItem(SESSION_KEY.CHARACTER_SOURCE);

      trackEvent(TRACK_EVENT.PAGE_CHARACTER_DETAIL, {
        look_name: iData.look_name,
        name: iData.name,
        tags: iData.tags,
        source,
      });
    }
  },
  { immediate: true, flush: 'sync' },
);

const { data: xiheData } = useAsyncData(`${lookName.value}_xihe`, () =>
  getCharacterFromXihe({
    look_name: lookName.value as string,
    terminal_application: getEnv('area') === 'CN' ? 'youyan' : 'youyan_international',
  }).then(res => {
    deepOmit(res, [
      'asset_info_path',
      'material_ue_svn_path_hierachy',
      'mod_grp_hierachy',
      'skin_ue_svn_path_hierachy',
      'ue_asset_dir',
      'ue_mask_dir',
      'ue_skeleton_file_path',
      'ue_textures_dir',
      'render_image_oss',
    ]);
    return res;
  }),
);

function createWork(source: SOURCE) {
  trackEvent(TRACK_EVENT.CLICK_CHARACTER_USE, {
    source,
    tags: character.value?.tags,
    id: character.value?.id,
    look_name: lookName.value,
    name: character.value?.name,
  });
  if (character.value) {
    createOps('person', character.value);
  }
}

function changeImage(source: SOURCE) {
  trackEvent(TRACK_EVENT.CLICK_CHARACTER_EDIT, {
    source,
    tags: character.value?.tags,
    id: character.value?.id,
    look_name: lookName.value,
    name: character.value?.name,
  });
  if (character.value) {
    createOps('image', character.value);
  }
}

const handleTrail = () => {
  useUserInfo().value
    ? navigateTo('/workspace')
    : navigateTo(
        `/login?redirect=${encodeURIComponent(encodeURIComponent(`${location.origin}/workspace`))}`,
      );
};

provide('createWork', createWork);

provide('changeImage', changeImage);

useWatchPageSeoData(calData);

onMounted(async () => {
  await paymentStore.fetchDiscount();
});
</script>
<style lang="scss">
.character-detail__header {
  .header {
    background-color: var(--x-white-1);
  }
}
.character-detail {
  padding-top: 64px;
  padding-bottom: 0;
  color: var(--x-text-1);
  @media (max-width: 680px) {
    padding-top: 0;
    padding-bottom: 80px;
  }
}
</style>
